import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Home as HomeIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';

export const Registration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: 'info',
    message: ''
  });

  // Handle form field changes
  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    
    // Clear alert when user starts typing
    if (alert.show) {
      setAlert({ show: false, type: 'info', message: '' });
    }
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Function to create user role in Firestore
  const createUserRole = async (userId, email) => {
    try {
      const userDocRef = doc(db, "users", userId);
      
      const userData = {
        role: "user",
        email: email,
        userId: userId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await setDoc(userDocRef, userData);
      console.log(`✅ User role created for ${userId} with role: user`);
      return true;
    } catch (error) {
      console.error("Error creating user role:", error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!formData.email.trim() || !formData.password.trim() || !formData.confirmPassword.trim()) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Bitte füllen Sie alle Felder aus.'
      });
      return;
    }

    if (!validateEmail(formData.email)) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
      });
      return;
    }

    if (formData.password.length < 6) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Das Passwort muss mindestens 6 Zeichen lang sein.'
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Die Passwörter stimmen nicht überein.'
      });
      return;
    }

    setLoading(true);

    try {
      // Create user with Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      
      const user = userCredential.user;
      console.log('User registered successfully:', user.uid);
      
      // Create user role in Firestore
      await createUserRole(user.uid, formData.email);
      
      setAlert({
        show: true,
        type: 'success',
        message: `Registrierung erfolgreich! Willkommen! Sie sind jetzt angemeldet.`
      });
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        confirmPassword: ''
      });
      
      // Redirect to main app after successful registration
      setTimeout(() => {
        navigate('/home');
      }, 2000);
      
    } catch (error) {
      console.error('Error during registration:', error);
      
      // Handle specific Firebase errors
      let errorMessage = 'Fehler bei der Registrierung';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Diese E-Mail-Adresse wird bereits verwendet. Bitte verwenden Sie eine andere E-Mail oder melden Sie sich an.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Ungültige E-Mail-Adresse.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Das Passwort ist zu schwach. Bitte wählen Sie ein stärkeres Passwort.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Registrierung ist derzeit nicht verfügbar. Bitte kontaktieren Sie den Administrator.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
          break;
        default:
          errorMessage = `Registrierungsfehler: ${error.message}`;
      }
      
      setAlert({
        show: true,
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: ''
    });
    setAlert({
      show: false,
      type: 'info',
      message: ''
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonAddIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom>
            Registrierung
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Erstellen Sie ein neues Konto, um Zugang zu unserem System zu erhalten.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          {/* Email */}
          <TextField
            fullWidth
            label="E-Mail-Adresse"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            margin="normal"
            required
            variant="outlined"
            helperText="Geben Sie eine gültige E-Mail-Adresse ein"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
          />

          {/* Password */}
          <TextField
            fullWidth
            label="Passwort"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange('password')}
            margin="normal"
            required
            variant="outlined"
            helperText="Passwort muss mindestens 6 Zeichen lang sein"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Confirm Password */}
          <TextField
            fullWidth
            label="Passwort bestätigen"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            margin="normal"
            required
            variant="outlined"
            helperText="Geben Sie das Passwort erneut ein"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Password Match Indicator */}
          {formData.password && formData.confirmPassword && (
            <Box sx={{ mt: 1 }}>
              {formData.password === formData.confirmPassword ? (
                <Typography variant="body2" color="success.main">
                  ✓ Passwörter stimmen überein
                </Typography>
              ) : (
                <Typography variant="body2" color="error.main">
                  ✗ Passwörter stimmen nicht überein
                </Typography>
              )}
            </Box>
          )}

          {/* Information Box */}
          <Box 
            sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: 'info.main', 
              color: 'info.contrastText',
              borderRadius: 1
            }}
          >
            <Typography variant="body2">
              <strong>Hinweis:</strong> Nach erfolgreicher Registrierung werden Sie automatisch angemeldet 
              und erhalten Zugang zu unserem System mit Standard-Benutzerrechten.
            </Typography>
          </Box>

          {/* Alert above submit button */}
          {alert.show && (
            <Alert severity={alert.type} sx={{ mt: 3, mb: 2 }}>
              {alert.message}
            </Alert>
          )}

          {/* Submit buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={<PersonAddIcon />}
              size="large"
              fullWidth
            >
              {loading ? 'Registrierung läuft...' : 'Registrieren'}
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Zurücksetzen
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<LoginIcon />}
                onClick={() => navigate('/')}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Login
              </Button>
            </Box>
          </Box>

          {/* Home Button */}
          {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Zur Startseite
            </Button>
          </Box> */}
        </Box>
      </Paper>
    </Container>
  );
};

export default Registration;
